import React, { type RefObject, useEffect } from 'react';

import type { PreloadedEntryPoint } from 'react-relay';

import type { ModalDialogProps } from '@atlaskit/modal-dialog/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { applyInitialFocus } from '@atlaskit/top-layer/apply-initial-focus';
import { getFirstFocusable } from '@atlaskit/top-layer/get-first-focusable';

import type { ContentReadySignal } from '../use-content-ready-signal';
import { WaitForEntryPointLoad } from '../wait-for-entry-point-load';

type FocusInsideOnLoadProps = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	entryPointReference: PreloadedEntryPoint<any>;
	autoFocusRef?: ModalDialogProps['autoFocus'];
	modalRef: RefObject<HTMLElement>;
	waitForContentReady: ContentReadySignal['waitForContentReady'];
};

function FocusInsideOnLoadInner({
	autoFocusRef,
	modalRef,
	waitForContentReady,
}: Omit<FocusInsideOnLoadProps, 'entryPointReference'>): null {
	useEffect(() => {
		const modalElement = modalRef.current;
		if (!modalElement) {
			return;
		}
		const focusContent = () => {
			if (fg('platform_dst-automated_focus')) {
				const result = applyInitialFocus({
					container: modalElement,
					// Modal Dialog permits a ref whose current value is `undefined`; the
					// shared focus contract treats that the same as an unset ref.
					initialFocusRef: autoFocusRef as RefObject<HTMLElement | null> | undefined,
					role: 'dialog',
				});
				if (!result.applied) {
					modalElement.focus();
				}
				return;
			}

			// If ref provided, use that first. Else find first focusable element or
			// container if not exists
			if (autoFocusRef?.current) {
				autoFocusRef.current.focus();
				return;
			}

			const firstFocusable = getFirstFocusable({ container: modalElement });
			if (firstFocusable) {
				firstFocusable.focus();
				return;
			}

			modalElement.focus();
		};

		if (!fg('platform_dst-trigger-focus-after-content')) {
			focusContent();
			return;
		}

		return waitForContentReady(focusContent);
	}, [autoFocusRef, modalRef, waitForContentReady]);

	return null;
}

/**
 * Moves focus into the modal once the entry point has finished loading, so
 * makers don't have to manage focus manually after the loading fallback is
 * replaced by the final experience.
 *
 * If the modal was opened with an `autoFocus` ref, focus is moved to that
 * element. Otherwise, focus is moved to the first focusable element inside
 * the modal dialog, or the dialog itself.
 */
export function FocusInsideOnLoad({
	entryPointReference,
	autoFocusRef,
	modalRef,
	waitForContentReady,
}: FocusInsideOnLoadProps): React.JSX.Element {
	return (
		<WaitForEntryPointLoad entryPointReference={entryPointReference}>
			<FocusInsideOnLoadInner
				autoFocusRef={autoFocusRef}
				modalRef={modalRef}
				waitForContentReady={waitForContentReady}
			/>
		</WaitForEntryPointLoad>
	);
}
